import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Music, Trophy, Ticket, ArrowRight, Guitar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/lib/api';
import { PublicHeader, PublicFooter } from '@/components/PublicShell';
import { StageStatusBadge, BandStatusBadge, formatDatePt, formatDateShortPt } from '@/components/Badges';

export default function HomePage() {
  const [settings, setSettings] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [bands, setBands] = useState([]);
  const [results, setResults] = useState([]);

  useEffect(() => {
    Promise.allSettled([
      api.get('/public/settings').then((r) => setSettings(r.data)),
      api.get('/public/upcoming').then((r) => setUpcoming(r.data)),
      api.get('/public/bands').then((r) => setBands(r.data)),
      api.get('/public/results').then((r) => setResults(r.data)),
    ]);
  }, []);

  const heroBg = settings?.cover_image_url ||
    'https://images.unsplash.com/photo-1544427920-c49ccfb85579?crop=entropy&cs=srgb&fm=jpg&q=85';

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PublicHeader />

      {/* HERO */}
      <section className="relative overflow-hidden g66-grain" data-testid="home-hero">
        <div className="absolute inset-0">
          <img src={heroBg} alt="Palco" className="w-full h-full object-cover" />
          <div className="absolute inset-0 g66-hero-overlay" />
        </div>
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 py-24 sm:py-32">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-black/50 backdrop-blur px-3 py-1 text-xs uppercase tracking-widest text-[var(--g66-amber)]">
              <Guitar className="w-3.5 h-3.5" /> {settings?.championship_name || 'Campeonato de Bandas'}
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white mt-4 text-shadow-lg leading-none">
              TODA QUINTA-FEIRA<br />
              UM VENCEDOR!<br />
              <span className="text-[var(--g66-amber)]">O PÚBLICO DECIDE.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-white/80">
              {settings?.home_text || 'Duas bandas no palco. Uma noite. Uma vencedora escolhida pelo público presente no Pub Garage 66.'}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/calendario">
                <Button data-testid="home-hero-primary-cta" size="lg" className="bg-[var(--g66-amber)] text-black hover:bg-[var(--g66-amber-2)]">
                  Ver próxima disputa <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/regulamento">
                <Button data-testid="home-hero-secondary-cta" size="lg" variant="secondary">
                  Como funciona a votação
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* NEXT BATTLE */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 -mt-10 relative z-10">
        {upcoming ? (
          <Card data-testid="next-battle-card" className="border border-border bg-card shadow-[0_10px_30px_rgba(0,0,0,0.45)]">
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-[var(--g66-amber)]" />
                  <div>
                    <div className="font-display text-2xl text-white">Próxima Disputa</div>
                    <div className="text-sm text-muted-foreground">{formatDatePt(upcoming.date)} • {upcoming.start_time || '21:00'}</div>
                  </div>
                </div>
                <StageStatusBadge status={upcoming.status} />
              </div>
              {upcoming.bands_count === 3 ? (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <BandBlock band={upcoming.band_a} side="A" />
                  <BandBlock band={upcoming.band_b} side="B" />
                  <BandBlock band={upcoming.band_c} side="C" />
                </div>
              ) : (
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-4 items-center">
                  <BandBlock band={upcoming.band_a} side="A" />
                  <div className="text-center font-display text-3xl text-[var(--g66-red)]">VS</div>
                  <BandBlock band={upcoming.band_b} side="B" />
                </div>
              )}
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to={`/etapas/${upcoming.slug}`}>
                  <Button data-testid="next-battle-cta" className="bg-[var(--g66-amber)] text-black hover:bg-[var(--g66-amber-2)]">
                    Ver detalhes da etapa <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <div className="text-xs text-muted-foreground self-center">
                  A votação abre após as apresentações. Cada comanda permite 1 voto.
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <Music className="h-8 w-8 mx-auto text-muted-foreground" />
              <p className="mt-2 text-muted-foreground">Nenhuma próxima disputa agendada no momento.</p>
            </CardContent>
          </Card>
        )}
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-14">
        <div className="font-display text-3xl text-white">COMO FUNCIONA</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          {[
            { icon: Ticket, title: 'Receba sua comanda', desc: 'Ao entrar no Pub você recebe uma comanda numerada com um QR Code único.' },
            { icon: Music, title: 'Curta as apresentações', desc: 'Duas bandas sobem ao palco em uma noite de rock inesquecível.' },
            { icon: Trophy, title: 'Escaneie e vote', desc: 'Após os shows, escaneie o QR da sua comanda. Cada comanda = 1 voto.' },
          ].map((step, i) => (
            <Card key={i} className="border border-border bg-card">
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-lg bg-[color:hsla(42,92%,52%,0.14)] flex items-center justify-center text-[var(--g66-amber)]">
                  <step.icon className="w-5 h-5" />
                </div>
                <div className="mt-3 font-display text-xl text-white">{step.title}</div>
                <div className="mt-1 text-sm text-muted-foreground leading-relaxed">{step.desc}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* RECENT RESULTS */}
      {results.length > 0 && (
        <section className="mx-auto max-w-6xl px-4 sm:px-6 py-6">
          <div className="flex items-baseline justify-between">
            <div className="font-display text-3xl text-white">ÚLTIMOS VENCEDORES</div>
            <Link to="/resultados" className="text-sm text-[var(--g66-amber)] hover:underline">Ver todos</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {results.slice(0, 6).map((s) => (
              <Link key={s.id} to={`/etapas/${s.slug}`} className="block">
                <Card className="card-hover border border-border bg-card" data-testid={`result-card-${s.slug}`}>
                  <CardContent className="p-5">
                    <div className="text-xs text-muted-foreground">{formatDateShortPt(s.date)}</div>
                    <div className="font-semibold text-white mt-1">{s.title}</div>
                    <div className="mt-3 flex items-center gap-2">
                      <Trophy className="w-4 h-4 text-[var(--g66-amber)]" />
                      <div className="font-display text-xl text-[var(--g66-amber)]">{s.winner_band?.name || 'A definir'}</div>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {s.band_a?.name} {s.band_a_votes ?? 0} — {s.band_b_votes ?? 0} {s.band_b?.name}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* BANDS */}
      {bands.length > 0 && (
        <section className="mx-auto max-w-6xl px-4 sm:px-6 py-6">
          <div className="flex items-baseline justify-between">
            <div className="font-display text-3xl text-white">BANDAS PARTICIPANTES</div>
            <Link to="/bandas" className="text-sm text-[var(--g66-amber)] hover:underline">Ver todas</Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
            {bands.slice(0, 8).map((b) => (
              <Link key={b.id} to={`/bandas/${b.slug}`} className="block">
                <Card className="card-hover border border-border bg-card overflow-hidden">
                  <div className="aspect-square bg-gradient-to-br from-[#1a1e2c] to-[#0b0c0f] flex items-center justify-center">
                    {b.logo_url ? (
                      <img src={b.logo_url} alt={b.name} className="w-full h-full object-cover" />
                    ) : (
                      <Guitar className="w-10 h-10 text-muted-foreground" />
                    )}
                  </div>
                  <CardContent className="p-3">
                    <div className="text-sm font-semibold text-white line-clamp-1">{b.name}</div>
                    <div className="text-xs text-muted-foreground line-clamp-1">{b.city}{b.state ? `/${b.state}` : ''}</div>
                    <div className="mt-2"><BandStatusBadge status={b.status} /></div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </section>
      )}

      <PublicFooter settings={settings} />
    </div>
  );
}

function BandBlock({ band, side }) {
  return (
    <div className="rounded-xl border border-border bg-[var(--g66-surface)] p-4 flex items-center gap-3">
      <div className="w-14 h-14 rounded-lg bg-[#0b0c0f] flex items-center justify-center overflow-hidden border border-border">
        {band?.logo_url ? <img src={band.logo_url} alt={band.name} className="w-full h-full object-cover" /> : <Guitar className="w-6 h-6 text-muted-foreground" />}
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Banda {side}</div>
        <div className="font-display text-xl text-white truncate">{band?.name || 'A definir'}</div>
        <div className="text-xs text-muted-foreground truncate">{band?.city}</div>
      </div>
    </div>
  );
}
